import { Label, Tooltip } from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

import {
  Submission,
  answerTone,
  answeredRatio,
  npsGroup,
  respondentHandle,
  respondentLabel
} from '@app/model/Survey';
import { SubmissionDetail, answeredPercent } from '@app/Classes/SurveyRespondent';
import { ddMMyyyyFormat } from '@app/utils/functions';

export interface SurveyRespondentsProps {
  submissions: readonly Submission[];
}

/** The colour PatternFly gives a Net Promoter group. Grey for passive: it is neither. */
const NPS_COLOUR = { promoter: 'green', passive: 'grey', detractor: 'red' } as const;

/**
 * One row per submission, expanding to that respondent's answers in full.
 *
 * The layout closest to the data as it arrives, and the only one that puts a name to an answer. Use
 * it to read one student - who they are, what they scored the class, what they wrote - which is the
 * question the other two tabs cannot answer at all. What it is worst at is the question they are
 * best at: "how did the class do on the labs" means opening every row and holding eight numbers in
 * your head.
 *
 * **Identified data throughout, so `admin` only** - the endpoint behind it refuses everybody else,
 * and ClassSurvey does not render this tab without the role.
 *
 * Rows are in the order the service returned them, which is by id, which is the order they were
 * imported. That is also the order the matrix numbers its columns, so R4 here is R4 there.
 */
export const SurveyRespondents: React.FunctionComponent<SurveyRespondentsProps> = ({ submissions }) => {
  // Every row closed to begin with. Opening one is cheap, and eight open cards is not a list.
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());

  const toggle = (uuid: string) =>
    setExpanded(open => {
      const next = new Set(open);
      if (!next.delete(uuid)) next.add(uuid);
      return next;
    });

  return (
    <Table aria-label="Survey respondents" variant="compact">
      <Thead>
        <Tr>
          <Th screenReaderText="Expand the submission" />
          <Th>Respondent</Th>
          <Th>Company</Th>
          <Th>Answered from</Th>
          <Th>Overall</Th>
          <Th>NPS</Th>
          <Th>
            <Tooltip content="How much of the survey this application knows about the student answered.">
              <span>Answered</span>
            </Tooltip>
          </Th>
          <Th>Submitted</Th>
        </Tr>
      </Thead>

      {submissions.map((submission, index) => {
        const isExpanded = expanded.has(submission.uuid);
        const nps = submission.nps;

        return (
          <Tbody key={submission.uuid} isExpanded={isExpanded}>
            <Tr>
              <Td
                expand={{
                  rowIndex: index,
                  isExpanded,
                  onToggle: () => toggle(submission.uuid),
                  expandId: `submission-${submission.uuid}`
                }}
              />
              <Td dataLabel="Respondent">
                <span className="cas-survey-handle">{respondentHandle(index)}</span>{' '}
                {respondentLabel(submission, index)}
              </Td>
              <Td dataLabel="Company">{submission.company ?? <span className="cas-survey-muted">—</span>}</Td>
              <Td dataLabel="Answered from">
                {submission.country?.alpha2 ?? <span className="cas-survey-muted">—</span>}
                {submission.language && <span className="cas-survey-muted"> · {submission.language.code}</span>}
              </Td>
              <Td dataLabel="Overall">
                {submission.overallRate === undefined || submission.overallRate === null ? (
                  <span className="cas-survey-muted">—</span>
                ) : (
                  <span className={`cas-survey-cell cas-tone-${answerTone(submission.overallRate, 'rating')}`}>
                    {submission.overallRate}
                  </span>
                )}
              </Td>
              <Td dataLabel="NPS">
                {nps === undefined || nps === null ? (
                  <span className="cas-survey-muted">—</span>
                ) : (
                  <Label isCompact color={NPS_COLOUR[npsGroup(nps)]}>
                    {nps} · {npsGroup(nps)}
                  </Label>
                )}
              </Td>
              <Td dataLabel="Answered">{answeredPercent(answeredRatio(submission))}</Td>
              <Td dataLabel="Submitted">
                {ddMMyyyyFormat(submission.surveyDate) || <span className="cas-survey-muted">—</span>}
              </Td>
            </Tr>
            {isExpanded && (
              <Tr isExpanded>
                <Td />
                <Td dataLabel="Submission" colSpan={7}>
                  <ExpandableRowContent>
                    <SubmissionDetail submission={submission} index={index} />
                  </ExpandableRowContent>
                </Td>
              </Tr>
            )}
          </Tbody>
        );
      })}
    </Table>
  );
};
